import { useState, useRef, useEffect } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import styled from "@emotion/styled";
import { IconSend, IconX, IconChevronLeft, IconChevronRight, IconLoader2 } from "@tabler/icons-react";
import ApplicationNavbar from "../ApplicationNavbar";
import TesseractLoader from "../ui/tesseract-loader";
import Markdown from "react-markdown";
import Modal from "../Modal";
import { BACKEND_URL } from "../../hooks/useOasisBackend";
import React from "react";

const ChatFontContainer = styled.div`
    font-family: "Quicksand", sans-serif;
    font-optical-sizing: auto;
    font-weight: 200;
    font-style: normal;
    color: #fff;

    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }
    
    .no-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
`

const SearchModal = ({text_results}: {text_results: {text: string, document_id: string, chunk_id: string}[]}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    return <>
        <Modal isOpen={isModalOpen} onClose={() => {
            setIsModalOpen(false)
        }}>
            <div className="flex flex-col gap-1 bg-gray-800 text-white rounded-md p-4 overflow-y-auto max-h-[60vh]">
                <div className="flex flex-row justify-end items-center w-full">
                    <IconX className="text-white" onClick={() => {
                        setIsModalOpen(false)
                    }} />
                </div>
                <div className="flex flex-col gap-4">
                    {text_results.map((result: any) => {
                            return <div>
                            <p>Text: {result.text}</p>
                            <p>Document ID: {result.document_id}</p>
                            <p>Chunk ID: {result.chunk_id}</p>
                        </div>
                    })}
                </div>
        </div>
    </Modal>
        <button className="text-white bg-gray-400 rounded-md p-2" onClick={() => {
            setIsModalOpen(true)
        }}>
            View Full Results
        </button>
    </>
}

const SearchToolResultRenderer = ({results}: {results: any}) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollContainer = React.useRef<HTMLDivElement>(null);
    
    // Handle safe parsing of results
    let parsedResults = [];
    try {
        if (typeof results === 'string') {
            parsedResults = JSON.parse(results);
        } else {
            parsedResults = results || [];
        }
    } catch (err) {
        console.error("Failed to parse search results:", err);
        parsedResults = [];
    }
    
    const checkScrollability = () => {
        if (scrollContainer.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
        }
    };
    
    React.useEffect(() => {
        checkScrollability();
        
        // Add resize observer to recheck when container size changes
        const resizeObserver = new ResizeObserver(() => {
            checkScrollability();
        });
        
        if (scrollContainer.current) {
            resizeObserver.observe(scrollContainer.current);
            
            // Add scroll event listener
            scrollContainer.current.addEventListener('scroll', checkScrollability);
        }
        
        // Cleanup
        return () => {
            if (scrollContainer.current) {
                scrollContainer.current.removeEventListener('scroll', checkScrollability);
                resizeObserver.disconnect();
            }
        };
    }, [parsedResults]);
    
    const scrollLeft = () => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollBy({ left: -300, behavior: 'smooth' });
            setScrollPosition(scrollContainer.current.scrollLeft - 300);
            checkScrollability();
        }
    };
    
    const scrollRight = () => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollBy({ left: 300, behavior: 'smooth' });
            setScrollPosition(scrollContainer.current.scrollLeft + 300);
            checkScrollability();
        }
    };
    
    // Define proper interface for product data
    interface ProductData {
        name: string;
        description: string;
        images: string[];
        documentId: string;
        chunkCount: number;
        chunks: string[];
    }
    
    // Helper to safely extract product data from text field that might be JSON
    const extractProductData = (result: any): ProductData => {
        if (!result) return { 
            name: "Unknown", 
            description: "No description available", 
            images: [], 
            documentId: "Unknown",
            chunkCount: 0,
            chunks: []
        };
        
        const productData: ProductData = { 
            name: "Unknown", 
            description: "No description available", 
            images: [],
            documentId: result.document_id || "Unknown",
            chunkCount: 1,
            chunks: [result.chunk_id || result.chunk_index || "Unknown"]
        };
        
        try {
            // Check if result has a text field that might contain JSON
            if (result.text) {
                // Try to parse the text field as JSON
                try {
                    const parsedText = JSON.parse(result.text);
                    productData.name = parsedText.name || "Unknown";
                    productData.description = parsedText.description || parsedText.ai_generated_image_description || "No description available";
                    productData.images = Array.isArray(parsedText.images) ? parsedText.images : [];
                } catch (e) {
                    // If the text isn't valid JSON, use it as the description
                    productData.description = result.text.substring(0, 100) + "...";
                }
            }
        } catch (err) {
            console.error("Error extracting product data:", err);
        }
        
        return productData;
    };
    
    // Process results to deduplicate by document_id and count chunks
    const processResults = (results: any[]): ProductData[] => {
        const documentMap = new Map<string, ProductData>();
        
        results.forEach(result => {
            const productData = extractProductData(result);
            const documentId = productData.documentId;
            
            if (documentMap.has(documentId)) {
                // Document already exists in map, update it
                const existingData = documentMap.get(documentId)!;
                existingData.chunkCount += 1;
                
                // Add chunk ID if it's not already in the list
                if (!existingData.chunks.includes(result.chunk_id || result.chunk_index || "Unknown")) {
                    existingData.chunks.push(result.chunk_id || result.chunk_index || "Unknown");
                }
            } else {
                // New document, add to map
                documentMap.set(documentId, productData);
            }
        });
        
        return Array.from(documentMap.values());
    };
    
    const uniqueResults = processResults(parsedResults);
    
    if (!uniqueResults || uniqueResults.length === 0) {
        return <div className="text-white p-4 bg-gray-800 rounded-lg">
            <p>No search results found</p>
        </div>;
    }

    return (
        <div className="my-6 animate-toolFadeIn">
            <p className="font-bold text-lg mb-3">Search Results ({parsedResults.length} chunks from {uniqueResults.length} documents)</p>
            <div className="relative flex items-center">
                {canScrollLeft && (
                    <button 
                        onClick={scrollLeft}
                        className="absolute -left-2 z-10 bg-gray-800 hover:bg-gray-700 p-2 rounded-full shadow-lg text-white transition-opacity"
                        aria-label="Scroll left"
                    >
                        <IconChevronLeft size={20} />
                    </button>
                )}
                
                <div 
                    ref={scrollContainer}
                    className="flex gap-4 overflow-x-auto py-4 px-12 scroll-smooth no-scrollbar w-full"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {uniqueResults.map((productData, index) => {
                        const { name, description, images, documentId, chunkCount } = productData;
                        
                        return (
                            <div key={index} className="flex-shrink-0 w-64 bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="p-4 flex flex-col h-full">
                                    <h3 className="font-semibold text-lg mb-2 truncate">{name}</h3>
                                    
                                    {images && images.length > 0 ? (
                                        <div className="mb-3 h-48 overflow-hidden rounded-md">
                                            <img 
                                                src={images[0]} 
                                                alt={name} 
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Image+Error';
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-3 h-48 bg-gray-700 rounded-md flex items-center justify-center">
                                            <p className="text-gray-400 text-sm">No image available</p>
                                        </div>
                                    )}
                                    
                                    <p className="text-sm mb-3 line-clamp-3 flex-grow">{description}</p>
                                    
                                    <div className="mt-auto pt-2 border-t border-gray-700">
                                        <div className="flex justify-between items-center mb-1">
                                            <p className="text-xs text-gray-400">Document ID:</p>
                                            <p className="text-xs font-medium">{String(documentId).substring(0, 10)}...</p>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <p className="text-xs text-gray-400">Chunks:</p>
                                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-700">
                                                {chunkCount}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {canScrollRight && (
                    <button 
                        onClick={scrollRight}
                        className="absolute -right-2 z-10 bg-gray-800 hover:bg-gray-700 p-2 rounded-full shadow-lg text-white transition-opacity"
                        aria-label="Scroll right"
                    >
                        <IconChevronRight size={20} />
                    </button>
                )}
            </div>
            
            <div className="mt-4 flex justify-center">
                <SearchModal text_results={parsedResults} />
            </div>
        </div>
    );
}

const TaskCompletedResultRenderer = ({results}: {results: any}) => {
    return <div className="animate-toolFadeIn">
        <div className={`flex flex-row justify-start`}>
            <p className="font-bold text-lg">Task Marked Completed</p>
        </div>
    </div>
}

const ToolCallRenderer = ({message}: {message: any}) => {
    return <div className={`flex flex-col text-white text-left justify-start animate-toolFadeIn`}>
        {message.content && 
        <div className={`flex flex-row justify-start items-center w-full`}>
                <div className={`flex flex-col py-4 rounded-md text-white`}>
                    <Markdown>{message.content}</Markdown>
                </div>
            </div>}
        {message.tool_calls.map((tool: any) => {
            return <div className={`flex flex-col text-white text-left justify-start rounded-md p-4 my-4 bg-gray-800 animate-toolFadeIn`}>
                <p className="font-bold">
                    Function Called: {tool.function.name}
                </p>
                <p className="text-sm text-wrap break-words">
                    Function Arguments: {tool.function.arguments}
                </p>
            </div>
        })}
    </div>
}

const EntityCollectionRenderer = ({results}: {results: any}) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollContainer = React.useRef<HTMLDivElement>(null);
    console.log("Results", results);
    
    // Handle safe parsing of results
    let parsedResults = [];
    try {
        if (typeof results === 'string') {
            parsedResults = JSON.parse(results);
        } else {
            parsedResults = results || [];
        }
    } catch (err) {
        console.error("Failed to parse entity results:", err);
        parsedResults = Array.isArray(results) ? results : [];
    }
    
    const checkScrollability = () => {
        if (scrollContainer.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
        }
    };
    
    React.useEffect(() => {
        checkScrollability();
        
        // Add resize observer to recheck when container size changes
        const resizeObserver = new ResizeObserver(() => {
            checkScrollability();
        });
        
        if (scrollContainer.current) {
            resizeObserver.observe(scrollContainer.current);
            
            // Add scroll event listener
            scrollContainer.current.addEventListener('scroll', checkScrollability);
        }
        
        // Cleanup
        return () => {
            if (scrollContainer.current) {
                scrollContainer.current.removeEventListener('scroll', checkScrollability);
                resizeObserver.disconnect();
            }
        };
    }, [parsedResults]);
    
    const scrollLeft = () => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollBy({ left: -300, behavior: 'smooth' });
            setScrollPosition(scrollContainer.current.scrollLeft - 300);
            checkScrollability();
        }
    };
    
    const scrollRight = () => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollBy({ left: 300, behavior: 'smooth' });
            setScrollPosition(scrollContainer.current.scrollLeft + 300);
            checkScrollability();
        }
    };
    
    if (!parsedResults || parsedResults.length === 0) {
        return <div className="text-white p-4 bg-gray-800 rounded-lg">
            <p>No entity data available</p>
        </div>;
    }
    
    return (
        <div className="my-6 animate-toolFadeIn">
            <p className="font-bold text-lg mb-3">Entities Found</p>
            <div className="relative flex items-center">
                {canScrollLeft && (
                    <button 
                        onClick={scrollLeft}
                        className="absolute -left-2 z-10 bg-gray-800 hover:bg-gray-700 p-2 rounded-full shadow-lg text-white transition-opacity"
                        aria-label="Scroll left"
                    >
                        <IconChevronLeft size={20} />
                    </button>
                )}
                
                <div 
                    ref={scrollContainer}
                    className="flex gap-4 overflow-x-auto py-4 px-12 scroll-smooth no-scrollbar w-full"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {parsedResults.map((result: any, index: number) => {
                        // Handle possible undefined content
                        const content = result?.truncated_content || {};
                        const name = content?.name || "Unnamed Entity";
                        const description = content?.description || "No description available";
                        const images = Array.isArray(content?.images) ? content.images : [];
                        const embeddingData = result?.oasis_embedding || {};
                        
                        return (
                            <div key={index} className="flex-shrink-0 w-64 bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                <div className="p-4 flex flex-col h-full">
                                    <h3 className="font-semibold text-lg mb-2 truncate">{name}</h3>
                                    
                                    {images.length > 0 ? (
                                        <div className="mb-3 h-48 overflow-hidden rounded-md">
                                            <img 
                                                src={images[0]} 
                                                alt={name} 
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Image+Error';
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-3 h-48 bg-gray-700 rounded-md flex items-center justify-center">
                                            <p className="text-gray-400 text-sm">No image available</p>
                                        </div>
                                    )}
                                    
                                    <p className="text-sm mb-3 line-clamp-3 flex-grow">{description}</p>
                                    
                                    {embeddingData && Object.keys(embeddingData).length > 0 && (
                                        <div className="mt-auto pt-2 border-t border-gray-700 max-h-48 overflow-y-auto">
                                            <div className="flex flex-col gap-1">
                                                {Object.entries(embeddingData).map(([key, value], idx) => {
                                                        return (
                                                            <div 
                                                                key={idx} 
                                                                className="flex justify-between items-center"
                                                            >
                                                                <span className="text-xs text-gray-300">{key}</span>
                                                                <span className="text-xs font-semibold">{String(value)}</span>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {canScrollRight && (
                    <button 
                        onClick={scrollRight}
                        className="absolute -right-2 z-10 bg-gray-800 hover:bg-gray-700 p-2 rounded-full shadow-lg text-white transition-opacity"
                        aria-label="Scroll right"
                    >
                        <IconChevronRight size={20} />
                    </button>
                )}
            </div>
        </div>
    );
}

const Chat = ({Database_id}: {Database_id: string}) => {
    const db = useOasisBackend()
    const [content, setContent] = useState<string>("");
    const [messages, setMessages] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [animatedText, setAnimatedText] = useState<string>("");
    const [isComplete, setIsComplete] = useState<boolean>(true);
    const chatContainerRef = useRef<HTMLDivElement>(null);

    // Function to scroll to the bottom of the chat
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Scroll to bottom when messages change or animated text updates
    useEffect(() => {
        scrollToBottom();
    }, [messages, animatedText]);

    const submitContent = async () => {
        if (!content.trim()) return;
        setIsComplete(false);
        
        // Add user message immediately
        const userMessage = {
            role: "user",
            content: content
        };
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setContent("");
        setAnimatedText("");
        
        try {
            // Get CSRF token
            const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
            
            // Use the streaming endpoint
            const response = await fetch(`${BACKEND_URL}/api/database/${Database_id}/chat-stream/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                body: JSON.stringify({
                    messages: newMessages,
                    temperature: 0.7,
                    model_name: "openai/gpt-4o" // You can make this configurable if needed
                }),
                credentials: 'include',
            });
            
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            
            let done = false;
            let toolCalls: {id: string, type: string, function: {name: string, arguments: string}}[] = [];
            let currentAnimatedText = "";
            let buffer = ""; // Buffer to accumulate partial JSON objects
            
            while (!done) {
                const { value, done: doneReading } = await reader?.read() ?? {};
                done = doneReading ?? true;
                
                if (value) {
                    // Add new chunk to buffer
                    const chunkValue = decoder.decode(value, { stream: true });
                    console.log("Chunk Value", chunkValue);
                    buffer += chunkValue;
                    
                    // Try to extract complete JSON objects from buffer
                    const parsedChunk = parseConcatenatedJsonObjects(buffer);
                    
                    // Process any complete JSON objects found
                    if (parsedChunk.length > 0) {
                        parsedChunk.forEach((chunk: any) => {
                            console.log("Chunk", chunk);
                            
                            // Remove the processed JSON from the buffer
                            // Find the position after the last complete JSON object
                            const lastJsonEnd = findLastCompleteJsonEnd(buffer);
                            if (lastJsonEnd > 0) {
                                buffer = buffer.substring(lastJsonEnd);
                            }
                            
                            if (chunk.role === "tool"){
                                console.log("Tool", chunk);
                                const text = currentAnimatedText;
                                if (text.trim().length > 0){
                                    setMessages((prevMessages) => [...prevMessages, {role: "assistant", content: text}, chunk]);
                                    currentAnimatedText = "";
                                    setAnimatedText("");
                                } else {
                                    setMessages((prevMessages) => [...prevMessages, chunk]);
                                }
                            } else if (chunk.tool_calls){
                                console.log("Tool Calls", chunk);
                                const text = currentAnimatedText;
                                if (text.trim().length > 0){
                                    setMessages((prevMessages) => [...prevMessages, {role: "assistant", content: text}, chunk]);
                                    currentAnimatedText = "";
                                    setAnimatedText("");
                                } else {
                                    setMessages((prevMessages) => [...prevMessages, chunk]);
                                }
                            } else if (chunk?.content){
                                console.log("Content", chunk);
                                currentAnimatedText += chunk.content;
                                setAnimatedText(prev => prev + chunk.content);
                            } else if (chunk?.completed && currentAnimatedText.trim().length > 0) {
                                console.log("Completed", chunk);
                                const finalText = currentAnimatedText;
                                if (finalText.trim().length > 0){
                                    setMessages((prevMessages) => [...prevMessages, {role: "assistant", content: finalText}]);
                                    setAnimatedText("");
                                    currentAnimatedText = "";
                                }
                                setIsComplete(true);
                            }
                        });
                    }
                }
            }
            
            // Process any remaining text in the buffer when stream is done
            if (buffer.trim() && currentAnimatedText.trim().length > 0) {
                const finalText = currentAnimatedText;
                setMessages((prevMessages) => [...prevMessages, {role: "assistant", content: finalText}]);
                setAnimatedText("");
            }
            
            setIsComplete(true);
            console.log("Tool Calls", toolCalls);
        } catch (error) {
            console.error("Error submitting content", error);
            setIsComplete(true);
        }
    };
    
    // Helper function to find the position after the last complete JSON object in a string
    function findLastCompleteJsonEnd(text: string): number {
        let lastCompleteEnd = 0;
        let currentPosition = 0;
        
        while (currentPosition < text.length) {
            // Find the beginning of a potential JSON object
            const startPos = text.indexOf('{', currentPosition);
            if (startPos === -1) break; // No more objects to find
            
            // Track brace count to find matching closing brace
            let openBraces = 1;
            let position = startPos + 1;
            let inString = false;
            let escaped = false;
            
            // Scan for the end of this JSON object
            while (position < text.length && openBraces > 0) {
                const char = text.charAt(position);
                
                if (inString) {
                    // Handle string escaping
                    if (char === '\\' && !escaped) {
                        escaped = true;
                    } else if (char === '"' && !escaped) {
                        inString = false;
                    } else {
                        escaped = false;
                    }
                } else {
                    // Not in a string, count braces
                    if (char === '{') {
                        openBraces++;
                    } else if (char === '}') {
                        openBraces--;
                    } else if (char === '"') {
                        inString = true;
                    }
                }
                
                position++;
            }
            
            // If we found a complete object
            if (openBraces === 0) {
                lastCompleteEnd = position;
                currentPosition = position;
            } else {
                // If we didn't find a complete object, move past the starting brace
                currentPosition = startPos + 1;
            }
        }
        
        return lastCompleteEnd;
    }
    
    function parseConcatenatedJsonObjects(input: string): unknown[] {
        const results: unknown[] = [];
        
        // Return empty array for empty input
        if (!input || input.trim() === '') {
            return results;
        }
        
        // Try to find objects in the text
        let currentPosition = 0;
        
        while (currentPosition < input.length) {
            try {
                // Find the beginning of a potential JSON object
                const startPos = input.indexOf('{', currentPosition);
                if (startPos === -1) break; // No more objects to find
                
                // Track brace count to find matching closing brace
                let openBraces = 1;
                let position = startPos + 1;
                let inString = false;
                let escaped = false;
                
                // Scan for the end of this JSON object
                while (position < input.length && openBraces > 0) {
                    const char = input.charAt(position);
                    
                    if (inString) {
                        // Handle string escaping
                        if (char === '\\' && !escaped) {
                            escaped = true;
                        } else if (char === '"' && !escaped) {
                            inString = false;
                        } else {
                            escaped = false;
                        }
                    } else {
                        // Not in a string, count braces
                        if (char === '{') {
                            openBraces++;
                        } else if (char === '}') {
                            openBraces--;
                        } else if (char === '"') {
                            inString = true;
                        }
                    }
                    
                    position++;
                }
                
                // If we found a complete object
                if (openBraces === 0) {
                    // Extract the object substring
                    const jsonString = input.substring(startPos, position);
                    
                    try {
                        // Try to parse it as JSON
                        const obj = JSON.parse(jsonString);
                        results.push(obj);
                    } catch (parseErr) {
                        console.error("Failed to parse JSON object:", parseErr);
                    }
                    
                    // Move position past this object
                    currentPosition = position;
                } else {
                    // If we didn't find a complete object, move past the starting brace
                    currentPosition = startPos + 1;
                }
            } catch (err) {
                // If any error occurs, move to the next character and try again
                console.error("Error processing JSON at position", currentPosition, err);
                currentPosition++;
            }
        }
        
        return results;
    }
    
    const renderMessage = (index: number, message: any) => {
        if (message.role === "tool") {
            if (message?.name === "semantic_search") {
                return <EntityCollectionRenderer results={message.content} />
            } else if (message?.name === "task_complete") {
                return <TaskCompletedResultRenderer results={message.content} />
            } else if (message?.name === "oasis_search") {
                return <EntityCollectionRenderer results={JSON.parse(message.content)} />
            } else {
                return <div>Unknown tool: {message.content}</div>
            }
        } else if (message.role === "assistant" && message.tool_calls) {
            return <ToolCallRenderer message={message} />
        } else if (message.role === "user") {
            return <div key={index} className={`flex flex-row justify-end items-center w-full`}>
                <div className={`flex flex-col py-4 rounded-md text-white`}>
                    {message.content}
                </div>
            </div>
        } else if (message.role === "assistant") {
            return <div key={index} className={`flex flex-row justify-start items-center w-full`}>
                <div className={`flex flex-col py-4 rounded-md text-white`}>
                    <Markdown>{message.content}</Markdown>
                </div>
            </div>
        } else if (message.role === "system") {
            return <div key={index} className={`flex flex-row justify-start items-center w-full`}>
                <div className={`flex flex-col py-4 rounded-md text-white`}>
                    <p className="text-sm font-bold">
                        System Prompt
                    </p>
                    {message.content}
                </div>
            </div>
        }
    }

    return(
        <div className="overflow-y-auto">
            <ApplicationNavbar className="text-white" openMenuClassName="bg-[#000b1e]" />
                    
                    <ChatFontContainer 
                        ref={chatContainerRef} 
                        className="w-full max-h-[60vh] p-4 md:p-16 overflow-y-scroll bg-transparent"
                    >
                        {
                            messages.map((message, index) => {
                                return renderMessage(index, message)
                            })
                        }
                        {!isComplete && !isLoading && (
                            <div className="flex flex-row justify-start items-center w-full">
                                <div className={`flex flex-col py-4 rounded-md text-white`}>
                                    <Markdown>{animatedText}</Markdown>
                                </div>
                            </div>
                        )}
                        {isLoading && <div className="flex flex-row justify-center items-center w-full">
                            <TesseractLoader />
                        </div>}
                    </ChatFontContainer>

                    <div className="flex flex-row absolute bottom-10 w-full h-[10vh] md:pb-4 justify-center items-center self-end">
                        <input 
                            className="w-11/12 h-[48p] bg-transparent text-white text-lg outline-none" 
                            type="text" 
                            value={content} 
                            onChange={(e) => setContent(e.target.value)} placeholder="Type a message" 
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  submitContent();
                                }
                            }}
                            disabled={isLoading || !isComplete}
                        />
                        <div className="flex items-center">
                            {!isComplete && (
                                <IconLoader2 className="text-white mr-2 animate-spin" size={18} />
                            )}
                            <button 
                                className="text-white inline" 
                                onClick={submitContent}
                                disabled={isLoading || !isComplete}
                            >
                                <IconSend />
                            </button>
                        </div>
                    </div>

    </div>
    
    )
}


export default Chat;